import { useEffect, useState, useRef } from "react";
import { useDialogFocus } from "../../../hooks/useDialogFocus";
import { useNavigate } from "react-router-dom";
import type { UnifiedContactResponse, BulkOutreachResult, ConnectorResponse, CrmMediaResponse } from "../../../types/crm";
import { bulkOutreach } from "../../../services/crmContacts";
import { getConnectors } from "../../../services/crmConnectors";
import { ProspectTypeahead } from "../../../components/ProspectTypeahead";
import type { ProspectOption } from "../../../components/ProspectTypeahead";
import { MediaBrowserModal } from "./MediaBrowserModal";
import { ModalCloseButton } from "../../../components/ModalCloseButton";
import "./BulkOutreachModal.css";

type Stage = "compose" | "confirm" | "sending" | "results";

type OutreachChannel = "Gmail" | "BlueBubbles" | "X";

// Mirrors CrmOutboundChannelResolver.ResolveCoachAsync on the API so the
// frontend's connector-required check matches the channel the backend will
// actually use at send time. Phone-preferred contacts (stored as Quo for
// legacy reasons) route through BlueBubbles, not the deprecated Quo path.
const resolveOutreachChannel = (c: {
  preferredContactMethod?: string;
  email?: string;
  phone?: string;
  xHandle?: string;
}): OutreachChannel | null => {
  const pref = c.preferredContactMethod;
  if (pref === "X" && c.xHandle?.trim()) return "X";
  if (pref === "Gmail" && c.email?.trim()) return "Gmail";
  if (pref === "Quo" && c.phone?.trim()) return "BlueBubbles";
  if (c.email?.trim()) return "Gmail";
  if (c.phone?.trim()) return "BlueBubbles";
  return null;
};

interface BulkOutreachModalProps {
  isOpen: boolean;
  contacts: UnifiedContactResponse[];
  onClose: () => void;
}

export const BulkOutreachModal = ({ isOpen, contacts, onClose }: BulkOutreachModalProps) => {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("compose");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [result, setResult] = useState<BulkOutreachResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectorErrors, setConnectorErrors] = useState<string[]>([]);
  const [validating, setValidating] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState<ProspectOption[]>([]);
  const [attachedMedia, setAttachedMedia] = useState<CrmMediaResponse | null>(null);
  const [showMediaBrowser, setShowMediaBrowser] = useState(false);

  // Media is only supported when exactly one player is selected
  const singlePlayer = selectedPlayers.length === 1 ? selectedPlayers[0] : null;

  useEffect(() => {
    if (!isOpen) {
      setStage("compose");
      setSubject("");
      setBody("");
      setResult(null);
      setError(null);
      setConnectorErrors([]);
      setValidating(false);
      setSelectedPlayers([]);
      setAttachedMedia(null);
    }
  }, [isOpen]);

  // Drop media attachment if user adds a second player
  useEffect(() => {
    if (selectedPlayers.length !== 1 && attachedMedia) setAttachedMedia(null);
  }, [selectedPlayers, attachedMedia]);

  // Pre-validate connectors when modal opens
  useEffect(() => {
    if (!isOpen || contacts.length === 0) return;

    let cancelled = false;
    setValidating(true);

    (async () => {
      try {
        const connectors: ConnectorResponse[] = await getConnectors();
        if (cancelled) return;

        const hasGmail = connectors.some((c) => c.connectorType === "Gmail" && c.status === "Active");
        const hasBlueBubbles = connectors.some((c) => c.connectorType === "BlueBubbles" && c.status === "Active");
        const hasX = connectors.some((c) => c.connectorType === "Twitter" && c.status === "Active");

        const channels = new Set(contacts.map(resolveOutreachChannel).filter((ch): ch is OutreachChannel => ch !== null));

        const errors: string[] = [];
        if (channels.has("Gmail") && !hasGmail) errors.push("Connect your Gmail to send this mass outreach");
        if (channels.has("BlueBubbles") && !hasBlueBubbles) errors.push("Connect BlueBubbles to send phone outreach to these contacts");
        if (channels.has("X") && !hasX) errors.push("Connect your X account to send this mass outreach");
        setConnectorErrors(errors);
      } catch {
        // Non-blocking
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, contacts]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && stage !== "sending") onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, stage]);

  const handleSend = async () => {
    setStage("sending");
    setError(null);
    try {
      const data = await bulkOutreach({
        contacts: contacts.map((c) => ({ contactId: c.id, contactType: c.contactType })),
        body,
        subject: subject.trim() || undefined,
        crmProspectProfileIds: selectedPlayers.length > 0 ? selectedPlayers.map((p) => p.id) : undefined,
        crmMediaId: attachedMedia?.id || undefined,
      });
      setResult(data);
      setStage("results");
    } catch {
      setError("Failed to send messages. Please try again.");
      setStage("confirm");
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && stage !== "sending") onClose();
  };

  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogFocus({ isOpen, dialogRef, handleEscape: false });

  if (!isOpen) return null;

  return (
    <div className="bulk-outreach-modal-overlay" onClick={handleOverlayClick}>
      <div ref={dialogRef} tabIndex={-1} className="bulk-outreach-modal" role="dialog" aria-modal="true" aria-label="Send bulk message">
        <div className="bulk-outreach-modal__header">
          <h2 className="bulk-outreach-modal__title">
            {stage === "compose" && `Send Message (${contacts.length})`}
            {stage === "confirm" && "Confirm Send"}
            {stage === "sending" && "Sending..."}
            {stage === "results" && "Send Results"}
          </h2>
          {stage !== "sending" && <ModalCloseButton onClick={onClose} />}
        </div>

        <div className="bulk-outreach-modal__body">
          {stage === "compose" && (
            <>
              <p className="bulk-outreach-modal__recipients">
                Sending to <strong>{contacts.length}</strong> contact{contacts.length !== 1 ? "s" : ""} via their preferred channel.
              </p>

              {connectorErrors.length > 0 && (
                <div className="bulk-outreach-modal__connector-errors">
                  {connectorErrors.map((err) => (
                    <div key={err} className="bulk-outreach-modal__connector-error">
                      <span>{err}.</span>
                      <button
                        type="button"
                        className="bulk-outreach-modal__connector-link"
                        onClick={() => navigate("/crm/connectors")}
                      >
                        Go to Connectors →
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="bulk-outreach-modal__field">
                <label className="bulk-outreach-modal__label">
                  On behalf of player(s) <span className="bulk-outreach-modal__optional">(optional)</span>
                </label>
                {selectedPlayers.length > 0 && (
                  <div className="bulk-outreach-modal__player-chips">
                    {selectedPlayers.map((p) => (
                      <span key={p.id} className="bulk-outreach-modal__player-chip">
                        {p.displayName}
                        <button
                          type="button"
                          className="bulk-outreach-modal__player-chip-remove"
                          aria-label={`Remove ${p.displayName}`}
                          onClick={() =>
                            setSelectedPlayers((prev) => prev.filter((x) => x.id !== p.id))
                          }
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <ProspectTypeahead
                  value={null}
                  onSelect={(p) => {
                    if (!p) return;
                    setSelectedPlayers((prev) =>
                      prev.some((x) => x.id === p.id) ? prev : [...prev, p]
                    );
                  }}
                  placeholder="Search players to link..."
                />
              </div>
              <div className="bulk-outreach-modal__field">
                <label className="bulk-outreach-modal__label" htmlFor="outreach-subject">
                  Subject <span className="bulk-outreach-modal__optional">(optional — for email)</span>
                </label>
                <input
                  id="outreach-subject"
                  type="text"
                  className="bulk-outreach-modal__input"
                  placeholder="e.g. Recruiting Update"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div className="bulk-outreach-modal__field">
                <label className="bulk-outreach-modal__label" htmlFor="outreach-body">
                  Message <span className="bulk-outreach-modal__required">*</span>
                </label>
                <textarea
                  id="outreach-body"
                  className="bulk-outreach-modal__textarea"
                  placeholder="Write your message here..."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={6}
                />
              </div>
              {selectedPlayers.length > 1 && (
                <p className="bulk-outreach-modal__hint">
                  Attach a video by selecting a single player.
                </p>
              )}
              {singlePlayer && (
                <div className="bulk-outreach-modal__field">
                  <label className="bulk-outreach-modal__label">
                    Attach video <span className="bulk-outreach-modal__optional">(optional)</span>
                  </label>
                  {attachedMedia ? (
                    <div className="bulk-outreach-modal__attached-media">
                      {attachedMedia.thumbnailUrl ? (
                        <img src={attachedMedia.thumbnailUrl} alt="" className="bulk-outreach-modal__attached-thumb" />
                      ) : (
                        <span>{attachedMedia.mediaType === "Video" ? "🎬" : "🖼"}</span>
                      )}
                      <span className="bulk-outreach-modal__attached-name">
                        {attachedMedia.originalFileName || attachedMedia.mediaType}
                      </span>
                      <button
                        type="button"
                        className="bulk-outreach-modal__attached-remove"
                        onClick={() => setAttachedMedia(null)}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="bulk-outreach-modal__attach-btn"
                      onClick={() => setShowMediaBrowser(true)}
                    >
                      Browse player media
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {stage === "confirm" && (
            <>
              {error && <p className="bulk-outreach-modal__error">{error}</p>}
              <p className="bulk-outreach-modal__confirm-text">
                You are about to send a message to <strong>{contacts.length}</strong> contact{contacts.length !== 1 ? "s" : ""}.
                Each contact will receive the message via their preferred channel (iMessage / SMS or email).
                {selectedPlayers.length > 0 && (
                  <> This outreach is on behalf of <strong>{selectedPlayers.map((p) => p.displayName).join(", ")}</strong>.</>
                )}
              </p>
              <div className="bulk-outreach-modal__preview">
                {subject && <p className="bulk-outreach-modal__preview-subject"><strong>Subject:</strong> {subject}</p>}
                <p className="bulk-outreach-modal__preview-body">{body}</p>
              </div>
            </>
          )}

          {stage === "sending" && (
            <div className="bulk-outreach-modal__sending">
              <div className="bulk-outreach-modal__spinner" />
              <p>Sending messages to {contacts.length} contact{contacts.length !== 1 ? "s" : ""}...</p>
            </div>
          )}

          {stage === "results" && result && (
            <>
              <div className="bulk-outreach-modal__summary">
                <span className="bulk-outreach-modal__summary-sent">{result.successCount} sent</span>
                {result.failureCount > 0 && (
                  <span className="bulk-outreach-modal__summary-failed">{result.failureCount} failed</span>
                )}
              </div>
              <ul className="bulk-outreach-modal__results-list">
                {result.results.map((r) => (
                  <li key={`${r.contactType}-${r.contactId}`} className={`bulk-outreach-modal__result-item bulk-outreach-modal__result-item--${r.status.toLowerCase()}`}>
                    <span className="bulk-outreach-modal__result-name">{r.displayName}</span>
                    <span className="bulk-outreach-modal__result-status">
                      {r.status === "Sent" && "Sent"}
                      {r.status === "Failed" && `Failed${r.error ? `: ${r.error}` : ""}`}
                      {r.status === "NoConnector" && "No connector"}
                      {r.status === "Unsendable" && "No contact info"}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="bulk-outreach-modal__footer">
          {stage === "compose" && (
            <>
              <button className="bulk-outreach-modal__btn bulk-outreach-modal__btn--secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="bulk-outreach-modal__btn bulk-outreach-modal__btn--primary"
                onClick={() => setStage("confirm")}
                disabled={!body.trim() || validating || connectorErrors.length > 0}
              >
                {validating ? "Checking..." : "Review"}
              </button>
            </>
          )}
          {stage === "confirm" && (
            <>
              <button className="bulk-outreach-modal__btn bulk-outreach-modal__btn--secondary" onClick={() => setStage("compose")}>
                Back
              </button>
              <button className="bulk-outreach-modal__btn bulk-outreach-modal__btn--primary" onClick={handleSend}>
                Send to {contacts.length}
              </button>
            </>
          )}
          {stage === "results" && (
            <button className="bulk-outreach-modal__btn bulk-outreach-modal__btn--primary" onClick={onClose}>
              Done
            </button>
          )}
        </div>
      </div>
      {singlePlayer && (
        <MediaBrowserModal
          isOpen={showMediaBrowser}
          crmProspectProfileId={singlePlayer.id}
          onSelect={(media) => {
            setAttachedMedia(media);
            setShowMediaBrowser(false);
          }}
          onClose={() => setShowMediaBrowser(false)}
        />
      )}
    </div>
  );
};
